import { User } from '../model';

/**
 * @desc Request-scoped identity resolved from a verified WorkOS access token.
 *
 * `userId` is the internal identifier (Mongo `users._id` and every `refs.userId`): the imported
 * Clerk ID for migrated users (carried in the `external_id` claim), otherwise the WorkOS user ID.
 * `roles` and `permissions` come straight from the token as WorkOS native RBAC claims — the
 * Mongo `users.roles` field is retained as data but is no longer the enforcement point.
 */
export type AuthState = {
  userId: string;
  workosUserId: string;
  sessionId: string | null;
  orgId: string | null;
  roles: string[];
  permissions: string[];
  email: string;
  firstName: string;
  lastName: string;
};

/** @desc Identity claims used to lazily provision/refresh the Mongo user document. */
export type UserClaims = Pick<AuthState, 'userId' | 'email' | 'firstName' | 'lastName'>;

/** @desc The token-derived role claim — `AuthState` satisfies this. */
export type RoleBearer = Pick<AuthState, 'roles'>;

/**
 * @desc The token-derived permission claim. Optional because principals minted before the
 * per-product roles landed (and the Mongo user document, which carries roles only) have none.
 */
export type PermissionBearer = { permissions?: string[] };

/** @desc Anything a guard can be asked about: the token claims, or a loaded user profile. */
export type AccessPrincipal = RoleBearer & PermissionBearer;

/**
 * @desc The caller identity a service needs to authorise a request: who they are plus the roles
 * their access token carries. The Mongo `users.roles` field is never the enforcement point.
 */
export type CallerIdentity = Pick<AuthState, 'userId' | 'roles' | 'permissions'>;

/** @desc Whether the principal's token claims carry the permission slug. */
export const hasPermission = (
  principal: PermissionBearer | null | undefined,
  permission: User.Permission,
): boolean => principal?.permissions?.includes(permission) ?? false;

/**
 * @desc Gate for the standalone agent deployment: the `agent:access` permission, carried by the
 * `agent-beta` and `internal-*` roles. A held (`agent-pending`) account carries no permission and
 * so no access.
 */
export function hasAgentAccess(principal: PermissionBearer | null | undefined): boolean {
  return hasPermission(principal, User.permissions.AgentAccess);
}

/**
 * @desc Gate for the full platform deployment: the `platform:access` permission, carried by the
 * platform product roles and the `internal-*` roles. Agent access never implies it — every grant
 * is explicit.
 */
export function hasPlatformAccess(principal: PermissionBearer | null | undefined): boolean {
  return hasPermission(principal, User.permissions.PlatformAccess);
}

/**
 * @desc The identity an agent turn is metered against for fair-use accounting (H2-176).
 *
 * Separate from {@link CallerIdentity} because metering and authorisation ask different questions:
 * roles decide what a caller may do, `orgId` decides whose budget the work comes out of. Carrying
 * the organisation from the outset is what lets org-wide and subscription-tier ceilings be added
 * to the quota policy later without another signature change through the service contract.
 */
export type MeteredIdentity = Pick<AuthState, 'userId' | 'orgId'>;

/**
 * @desc The single admin rule, shared by the backend guards, the tRPC admin procedure and the
 * portal. Absent principals (an unloaded profile, an anonymous request) are never admins.
 * Permission-only: the `admin:access` permission is carried by the `internal-admin` role; the
 * pre-H2-171 legacy `admin` role slug is no longer accepted (its holders were migrated to
 * `internal-admin` — `refactor-roles-internal-product-split`).
 */
export function isAdmin(principal: AccessPrincipal | null | undefined): boolean {
  if (!principal) return false;
  return hasPermission(principal, User.permissions.AdminAccess);
}

/**
 * @desc The admin panel's view gate (D21, ADR-29): the `admin:view` permission, carried by
 * `internal-admin-viewer` and `internal-admin`. Admits to the admin layout and the admin pages'
 * read procedures; every admin write stays on {@link isAdmin}. `admin:access` is accepted as well
 * — `internal-admin` carries both anyway, so the arm is belt-and-braces rather than a widening,
 * and an admin's session minted before `admin:view` was attached in WorkOS keeps the panel.
 *
 * Grants no Ario access: that is `canAutomate`, on the `automations:*` slugs alone.
 */
export function canViewAdmin(principal: AccessPrincipal | null | undefined): boolean {
  if (!principal) return false;
  return hasPermission(principal, User.permissions.AdminView) || isAdmin(principal);
}
